import {ImageService} from './base-service'

// {origin?}/bv/{accountId}/{encoded videoId}/poster.jpg, optionally followed by ?w={width}
const POSTER_URL_PATTERN =
    /^((?:https?:\/\/[^/?#]+)?\/bv\/[^/?#]+\/[^/?#]+\/poster\.jpg)(?:\?w=(\d+))?$/

/**
 * Service for the Brightcove poster endpoint on our own server, which resizes the poster
 * through its `w` query param.
 */
export class BrightcovePosterService extends ImageService {
    /**
     * Create a service instance if the URL points at our poster endpoint.
     * @param {string} url - Root-relative or absolute poster endpoint URL
     * @param {Object} _context - Context (not used for the poster endpoint)
     * @returns {BrightcovePosterService|null} Service instance or null
     */
    static create(url, _context) {
        if (!url || typeof url !== 'string') return null

        const match = url.match(POSTER_URL_PATTERN)
        if (!match) return null

        return new BrightcovePosterService(url, {baseUrl: match[1]})
    }

    generateUrl(width, _options = {}) {
        if (!Number.isInteger(width) || width <= 0) return null
        return `${this.baseInfo.baseUrl}?w=${width}`
    }

    getSupportedFormats() {
        // the endpoint redirects to Brightcove's JPEG renditions
        return ['image/jpeg']
    }

    generateSources(sizes) {
        if (!sizes || sizes.length === 0) {
            return [{type: 'image/jpeg', srcSet: this.url}]
        }

        const srcsetEntries = sizes
            .map((width) => {
                const url = this.generateUrl(width)
                return url ? `${url} ${width}w` : null
            })
            .filter(Boolean)

        return [{type: 'image/jpeg', srcSet: srcsetEntries.join(', ')}]
    }

    /**
     * Extract the requested width from a poster endpoint URL.
     * @param {string} url - Poster endpoint URL
     * @returns {number|null} Width in pixels or null if not found/invalid
     *
     * @example
     * service.extractWidthFromUrl('/bv/1234/ref%3Ahero/poster.jpg?w=768')
     * // Returns: 768
     */
    extractWidthFromUrl(url) {
        if (!url || typeof url !== 'string') return null

        const match = url.match(POSTER_URL_PATTERN)
        if (!match?.[2]) return null

        const width = parseInt(match[2], 10)
        return width > 0 ? width : null
    }
}
